import React, { useState } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Button,
    Menu,
    MenuItem,
    Select,
    FormControl,
    Popover,
    TextField,
    CircularProgress,
    Chip,
} from '@mui/material';
import {
    CalendarIcon,
    CustomDownloadIcon,
    CustomRefreshIcon,
    MonitorIcon,
    LiveCamIcon,
    ReportIssueIcon,
} from '../../../Icons/DashboardIcons';
import ReportButton from '../../reportButton/reportButton';

/**
 * Reusable Dashboard Header Component
 * Used by both TLDashboard and MBFDashboard
 * 
 * @param {string} title - Dashboard title (e.g., "Dashboard")
 * @param {string} lastUpdated - Last updated timestamp
 * @param {string} selectedPeriod - Currently selected period (Weekly, Monthly, Yearly)
 * @param {function} onPeriodChange - Callback when period changes
 * @param {function} onRefresh - Callback when refresh button is clicked
 * @param {function} onDownload - Callback when download button is clicked
 * @param {string} selectorLabel - Label for the dropdown selector (e.g., "Select TL" or "Select MBF")
 * @param {array} selectorOptions - Array of options for the dropdown [{id, name}]
 * @param {string} selectedOption - Currently selected option ID
 * @param {function} onOptionChange - Callback when dropdown option changes
 * @param {boolean} showSelector - Whether to show the dropdown selector
 * @param {boolean} useRangePicker - Whether to show date range picker instead of period selector
 * @param {object} dateRange - Selected date range {startDate, endDate}
 * @param {function} onDateRangeChange - Callback when date range changes
 * @param {boolean} downloading - Whether download is in progress
 * @param {boolean} simulationMode - Whether simulation mode is active
 * @param {boolean} showLiveFeedButton - Whether to show the Live Feed button
 * @param {function} onLiveFeedClick - Callback when Live Feed button is clicked
 * @param {boolean} isLiveFeedActive - Whether Live Feed is currently active view
 */
const DashboardHeader = ({
    title = 'Dashboard',
    lastUpdated = '',
    selectedPeriod = 'Weekly',
    onPeriodChange,
    onRefresh,
    onDownload,
    selectorLabel = 'Select TL',
    selectorOptions = [],
    selectedOption = '',
    onOptionChange,
    showSelector = true,
    useRangePicker = false,
    dateRange = { startDate: '', endDate: '' },
    onDateRangeChange,
    downloading = false,
    simulationMode = false,
    showLiveFeedButton = false,
    onLiveFeedClick,
    isLiveFeedActive = false,
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [datePickerAnchor, setDatePickerAnchor] = useState(null);
    const [tempDateRange, setTempDateRange] = useState({ startDate: '', endDate: '' });

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = (period) => {
        if (period && onPeriodChange) {
            onPeriodChange(period);
        }
        setAnchorEl(null);
    };

    const handleDatePickerOpen = (event) => {
        setTempDateRange(dateRange);
        setDatePickerAnchor(event.currentTarget);
    };

    const handleDatePickerClose = () => {
        setDatePickerAnchor(null);
    };

    const handleDateRangeApply = () => {
        if (onDateRangeChange) {
            onDateRangeChange(tempDateRange);
        }
        handleDatePickerClose();
    };

    const formatDateRangeDisplay = () => {
        if (dateRange.startDate && dateRange.endDate) {
            const start = new Date(dateRange.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            const end = new Date(dateRange.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            return `${start} - ${end}`;
        }
        return 'Select Range';
    };

    return (
        <Box
            sx={{
                bgcolor: '#ffffff',
                boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.05)',
                borderRadius: { xs: '6px', md: '8px' },
                padding: { xs: '12px 16px', sm: '14px 20px', md: '16px 24px' },
                mb: { xs: 2, md: 3 },
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 1.5, sm: 2 },
            }}
        >
            {/* Left side - Title and Date Badge */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: { xs: 1, md: 2 },
                    flexWrap: 'wrap',
                }}
            >
                <Typography
                    sx={{
                        fontSize: { xs: '18px', sm: '20px', md: '24px' },
                        fontWeight: 500,
                        fontFamily: '"Roboto-Medium", "Roboto", sans-serif',
                        color: '#333333',
                        lineHeight: '32px',
                    }}
                >
                    {title}
                </Typography>
                {simulationMode && (
                    <Chip
                        icon={<MonitorIcon color="#fff" />}
                        label="Simulation Mode"
                        size="small"
                        sx={{
                            bgcolor: '#f50057',
                            color: '#fff',
                            fontWeight: 'bold',
                            borderRadius: '4px',
                            '& .MuiChip-icon': { color: 'inherit', fontSize: '16px' },
                            mr: 1
                        }}
                    />
                )}
                {showLiveFeedButton && (
                    <Button
                        variant={isLiveFeedActive ? "contained" : "outlined"}
                        size="small"
                        onClick={onLiveFeedClick}
                        startIcon={<LiveCamIcon color={isLiveFeedActive ? "#fff" : "#007bff"} />}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 'bold',
                            borderRadius: '4px',
                            px: 2,
                            bgcolor: isLiveFeedActive ? '#007bff' : 'transparent',
                            color: isLiveFeedActive ? '#fff' : '#007bff',
                            border: '1px solid #007bff',
                            '&:hover': {
                                bgcolor: isLiveFeedActive ? '#0056b3' : 'rgba(0, 123, 255, 0.04)',
                                border: '1px solid #007bff',
                            }
                        }}
                    >
                        Live Feed
                    </Button>
                )}
                <Box
                    sx={{
                        bgcolor: '#f2f2f2',
                        borderRadius: '12px',
                        padding: { xs: '3px 6px', md: '4px 8px' },
                        display: { xs: 'none', sm: 'inline-flex' },
                        alignItems: 'center',
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: { xs: '10px', md: '12px' },
                            fontWeight: 400,
                            fontFamily: '"Roboto-Regular", "Roboto", sans-serif',
                            color: '#8c8c8c',
                            lineHeight: '16px',
                        }}
                    >
                        Last Updated on: {lastUpdated}
                    </Typography>
                </Box>
            </Box>

            {/* Right side - Selector and Buttons */}
            <Box
                sx={{
                    display: 'flex',
                    gap: { xs: '8px', md: '12px' },
                    alignItems: 'center',
                    flexWrap: 'wrap',
                }}
            >
                {/* Dropdown Selector (TL or MBF) */}
                {showSelector && selectorOptions.length > 0 && (
                    <FormControl size="small">
                        <Select
                            value={selectedOption}
                            onChange={(e) => onOptionChange && onOptionChange(e.target.value)}
                            displayEmpty
                            renderValue={(selected) => {
                                if (!selected) {
                                    return (
                                        <Typography sx={{ color: '#8c8c8c', fontSize: '14px' }}>
                                            {selectorLabel}
                                        </Typography>
                                    );
                                }
                                const option = selectorOptions.find((opt) => opt.id === selected);
                                return option ? option.name : selectorLabel;
                            }}
                            sx={{
                                minWidth: { xs: '120px', md: '140px' },
                                bgcolor: '#ffffff',
                                border: '1px solid #e6e6e6',
                                borderRadius: '8px',
                                boxShadow: '0px 2px 2px 0px rgba(0, 0, 0, 0.05)',
                                fontSize: { xs: '12px', md: '14px' },
                                color: '#8c8c8c',
                                '& .MuiOutlinedInput-notchedOutline': {
                                    border: 'none',
                                },
                                '& .MuiSelect-select': {
                                    padding: { xs: '6px 10px', md: '8px 12px' },
                                },
                                '&:hover': {
                                    bgcolor: '#f9f9f9',
                                },
                            }}
                        >
                            {selectorOptions.map((option) => (
                                <MenuItem
                                    key={option.id}
                                    value={option.id}
                                    sx={{ color: '#8C8C8C' }}
                                >
                                    {option.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}

                {/* Period Selector Button OR Date Range Picker */}
                {useRangePicker ? (
                    <>
                        <Button
                            variant="outlined"
                            endIcon={<CalendarIcon />}
                            onClick={handleDatePickerOpen}
                            sx={{
                                textTransform: 'none',
                                fontFamily: '"Roboto-Medium", "Roboto", sans-serif',
                                fontSize: { xs: '12px', md: '14px' },
                                fontWeight: 500,
                                lineHeight: '20px',
                                color: '#8c8c8c',
                                bgcolor: '#ffffff',
                                border: '1px solid #e6e6e6',
                                borderRadius: '8px',
                                boxShadow: '0px 2px 2px 0px rgba(0, 0, 0, 0.05)',
                                padding: { xs: '6px 10px', md: '8px 12px' },
                                minWidth: { xs: 'auto', md: '120px' },
                                '&:hover': {
                                    bgcolor: '#f9f9f9',
                                    border: '1px solid #e6e6e6',
                                },
                            }}
                        >
                            {formatDateRangeDisplay()}
                        </Button>
                        <Popover
                            open={Boolean(datePickerAnchor)}
                            anchorEl={datePickerAnchor}
                            onClose={handleDatePickerClose}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                        >
                            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, minWidth: '280px' }}>
                                <Typography sx={{ fontWeight: 500, color: '#333' }}>Select Date Range</Typography>
                                <TextField
                                    label="Start Date"
                                    type="date"
                                    value={tempDateRange.startDate}
                                    onChange={(e) => setTempDateRange({ ...tempDateRange, startDate: e.target.value })}
                                    InputLabelProps={{ shrink: true }}
                                    size="small"
                                    fullWidth
                                />
                                <TextField
                                    label="End Date"
                                    type="date"
                                    value={tempDateRange.endDate}
                                    onChange={(e) => setTempDateRange({ ...tempDateRange, endDate: e.target.value })}
                                    InputLabelProps={{ shrink: true }}
                                    size="small"
                                    fullWidth
                                />
                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                    <Button size="small" onClick={handleDatePickerClose}>Cancel</Button>
                                    <Button size="small" variant="contained" onClick={handleDateRangeApply}>Apply</Button>
                                </Box>
                            </Box>
                        </Popover>
                    </>
                ) : (
                    <>
                        <Button
                            variant="outlined"
                            endIcon={<CalendarIcon />}
                            onClick={handleMenuOpen}
                            sx={{
                                textTransform: 'none',
                                fontFamily: '"Roboto-Medium", "Roboto", sans-serif',
                                fontSize: { xs: '12px', md: '14px' },
                                fontWeight: 500,
                                lineHeight: '20px',
                                color: '#8c8c8c',
                                bgcolor: '#ffffff',
                                border: '1px solid #e6e6e6',
                                borderRadius: '8px',
                                boxShadow: '0px 2px 2px 0px rgba(0, 0, 0, 0.05)',
                                padding: { xs: '6px 10px', md: '8px 12px' },
                                minWidth: { xs: 'auto', md: '100px' },
                                '&:hover': {
                                    bgcolor: '#f9f9f9',
                                    border: '1px solid #e6e6e6',
                                },
                            }}
                        >
                            {selectedPeriod}
                        </Button>
                        <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={() => handleMenuClose(null)}
                        >
                            <MenuItem
                                onClick={() => handleMenuClose('Weekly')}
                                sx={{ color: '#8C8C8C' }}
                            >
                                Weekly
                            </MenuItem>
                            <MenuItem
                                onClick={() => handleMenuClose('Monthly')}
                                sx={{ color: '#8C8C8C' }}
                            >
                                Monthly
                            </MenuItem>
                            <MenuItem
                                onClick={() => handleMenuClose('Yearly')}
                                sx={{ color: '#8C8C8C' }}
                            >
                                Yearly
                            </MenuItem>
                        </Menu>
                    </>
                )}

                <ReportButton 
                    displayMode="navbar" 
                    icon={ReportIssueIcon}
                    sx={{
                        bgcolor: '#ffffff',
                        border: '1px solid #e6e6e6',
                        borderRadius: '8px',
                        padding: { xs: '6px', md: '8px' },
                        width: { xs: '32px', md: '36px' },
                        height: { xs: '32px', md: '36px' },
                        '&:hover': {
                            bgcolor: '#f9f9f9',
                        },
                    }}
                />

                {/* Download Button */}
                <IconButton
                    onClick={onDownload}
                    disabled={downloading}
                    sx={{
                        bgcolor: '#ffffff',
                        border: '1px solid #e6e6e6',
                        borderRadius: '8px',
                        padding: { xs: '6px', md: '8px' },
                        width: { xs: '32px', md: '36px' },
                        height: { xs: '32px', md: '36px' },
                        '&:hover': {
                            bgcolor: '#f9f9f9',
                        },
                        '&:disabled': {
                            bgcolor: '#f5f5f5',
                        },
                    }}
                >
                    {downloading ? (
                        <CircularProgress size={16} sx={{ color: '#666' }} />
                    ) : (
                        <CustomDownloadIcon />
                    )}
                </IconButton>

                {/* Refresh Button */}
                <IconButton
                    onClick={onRefresh}
                    sx={{
                        bgcolor: '#ffffff',
                        border: '1px solid #e6e6e6',
                        borderRadius: '8px',
                        padding: { xs: '6px', md: '8px' },
                        width: { xs: '32px', md: '36px' },
                        height: { xs: '32px', md: '36px' },
                        '&:hover': {
                            bgcolor: '#f9f9f9',
                        },
                    }}
                >
                    <CustomRefreshIcon />
                </IconButton>
            </Box>
        </Box>
    );
};

export default DashboardHeader;

